import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "page has-compressed-header" }
const _hoisted_2 = {
  key: 1,
  class: "section primary-header"
}
const _hoisted_3 = { class: "level container is-fluid" }
const _hoisted_4 = { class: "level-left" }
const _hoisted_5 = { class: "title is-4" }
const _hoisted_6 = { class: "level-right" }
const _hoisted_7 = { class: "level-item" }
const _hoisted_8 = { class: "level-item" }
const _hoisted_9 = {
  key: 2,
  class: "section content-container"
}
const _hoisted_10 = { class: "container is-fluid" }
const _hoisted_11 = { class: "columns" }
const _hoisted_12 = { class: "column is-6" }
const _hoisted_13 = { class: "preview-container" }
const _hoisted_14 = ["srcdoc"]

import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import Toast from '@/shared/services/toast';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import { emailApi } from '../services/emailApiService';
import * as ace from 'brace';
import 'brace/mode/html';
import 'brace/theme/monokai';
import 'brace/ext/language_tools';
import EditableText from '@/shared/components/EditableText.vue';
import { sanitizeHtml } from '@/shared/util/sanitize-html/sanitizeHtml';
import { Issues } from '@/shared/util/sanitize-html/types';
import { emailTokenPlaceholders } from '../services/emailTokenPlaceholders';
import { htmlFormatter } from '../services/htmlFormatter';
import { SANITIZE_OPTIONS } from '@/shared/util/sanitize-html/EmailTemplateSanitizeOptions';
import { useStore } from 'vuex';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const EDITOR_ID = 'email-template-editor';


export default /*@__PURE__*/_defineComponent({
  __name: 'EmailTemplate',
  setup(__props) {

const TOKENS = {
  messages: '{{ messages }}',
  senderName: '{{ senderName }}',
};
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const route = useRoute();
const router = useRouter();

const templateId = ref('');
const templateName = ref('');
const templateHtml = ref('');
const previewHtml = ref('');
const templateHtmlHasIssues = ref(false);
const templateChanged = ref(false);
const templateLoaded = ref(false);
const updateInProgress = ref(false);
const showDiscardChangesModel = ref(false);

let editor: ace.Editor;
let editorAnnotationTimeout: number;

onMounted(async () => {
  templateId.value = route.params.templateId as string;
  templateName.value = route.query.templateName as string;
  await loadTemplate();
  setupAceEditor();
});

onBeforeUnmount(() => {
  templateHtml.value = '';
  previewHtml.value = '';
  templateChanged.value = false;
});

async function loadTemplate() {
  if (templateId.value) {
    try {
      const htmlContent: string = await emailApi.getTemplateContent(currentWorkspace.value.id, templateId.value);
      templateHtml.value = htmlContent;
      setAceEditorValue(htmlContent);
      setPreviewValue(htmlContent);
      templateLoaded.value = true;
    } catch (error: any) {
      if (error.status !== 404) {
        handleSeriousError(error.message);
      }
    }
  } else {
    templateHtml.value = `<!DOCTYPE html>\r\n<html><head></head><body>${TOKENS.messages}</body></html>`;
    setAceEditorValue(TOKENS.messages);
    setPreviewValue(templateHtml.value);
    templateLoaded.value = true;
  }
}

function setupAceEditor() {
  if (isEditorReadyForSetup()) {
    const langTools = ace.acequire('ace/ext/language_tools');
    langTools.setCompleters([]);

    editor = ace.edit(EDITOR_ID);
    editor.getSession().setMode('ace/mode/html');
    editor.setTheme('ace/theme/monokai');
    editor.setShowPrintMargin(false);
    editor.session.on('change', (event: ace.EditorChangeEvent) => setTemplateHtmlOnChange());

    editor.setOptions({
      enableBasicAutocompletion: true,
    });

    const customCompleter = {
      getCompletions: (editor: ace.Editor, session: ace.IEditSession, pos: number, prefix: any, callback: any) => {
        const suggestions = [
          { caption: 'Content Placeholder', value: TOKENS.messages, meta: 'Metadata' },
          { caption: "Sender's Name Placeholder", value: TOKENS.senderName, meta: 'Metadata' },
        ];

        callback(null, suggestions);
      },
    };

    langTools.addCompleter(customCompleter);

    if (templateHtml.value) {
      setAceEditorValue(templateHtml.value);
    }

    editor.resize();
  }
}

function isEditorReadyForSetup(): boolean {
  return !editor && !!document.getElementById(EDITOR_ID);
}

function setTemplateHtmlOnChange() {
  const newContent: string = editor.session.getValue();

  if (templateHtml.value !== newContent) {
    updateTemplateHtml(newContent);
  }
}

function updateTemplateHtml(newContent: string) {
  templateHtml.value = newContent;

  setPreviewValue(getSafeHtml(newContent));
  templateChanged.value = true;
}

function setPreviewValue(html: string) {
  const whiteBackgroundStyle = '<style>body {background-color: white;}</style>';
  previewHtml.value = whiteBackgroundStyle + replaceTokens(html);
}

function setAceEditorValue(newValue: string) {
  if (editor) {
    editor.session.setValue(newValue);
  }
}

function replaceTokens(newContent: string): string {
  return newContent
    .replace(TOKENS.messages, emailTokenPlaceholders.getContentPlaceholder())
    .replace(TOKENS.senderName, emailTokenPlaceholders.getSenderNamePlaceholder());
}

function getSafeHtml(unCleaned: string): string {
  // https://www.npmjs.com/package/sanitize-html

  const response = sanitizeHtml(unCleaned, SANITIZE_OPTIONS);
  showEditorWarningsAfterDelay(response.issues);
  return response.output;
}

function showEditorWarningsAfterDelay(issues: Issues[]) {
  templateHtmlHasIssues.value = issues.length > 0;
  if (editorAnnotationTimeout) {
    clearTimeout(editorAnnotationTimeout);
  }
  editorAnnotationTimeout = window.setTimeout(() => showEditorWarnings(issues), 1000);
}

function showEditorWarnings(issues: Issues[]) {
  editor.getSession().setAnnotations(
    issues.map((issue) => {
      return {
        row: issue.line - 1,
        column: issue.column,
        text: issue.description,
        type: 'error', // also warning and information
      };
    }),
  );
}

function onNameChange(newName: string) {
  templateChanged.value = true;
  templateName.value = newName;
}

async function saveTemplate() {
  if (validateSaveRequest()) {
    try {
      updateInProgress.value = true;
      templateHtml.value = htmlFormatter.format(templateHtml.value);
      await callSaveApi();
      templateChanged.value = false;
      Toast.showSuccess('Template saved');
      goBackToEmailSettings();
    } catch (err: any) {
      Toast.showError(err.message);
    } finally {
      updateInProgress.value = false;
    }
  }
}

function callSaveApi(): Promise<void> {
  if (templateId.value) {
    return emailApi.saveEmailTemplate(
      currentWorkspace.value.id,
      templateId.value,
      templateName.value,
      templateHtml.value,
    );
  } else {
    return emailApi.createEmailTemplate(currentWorkspace.value.id, templateName.value, templateHtml.value);
  }
}

function validateSaveRequest(): boolean {
  if (!templateName.value) {
    Toast.showError('Template name cannot be empty');
    return false;
  }
  if (templateHtml.value.indexOf(TOKENS.messages) === -1) {
    Toast.showError(`Placeholder ${TOKENS.messages} must be present in template`);
    return false;
  }
  if (templateHtmlHasIssues.value) {
    Toast.showError(`HTML content contains issues, please resolve before saving`);
    return false;
  }
  return true;
}

function cancel() {
  if (templateChanged.value) {
    showDiscardChangesModel.value = true;
  } else {
    goBackToEmailSettings();
  }
}

function goBackToEmailSettings() {
  router.push({ name: 'email' });
}

return (_ctx: any,_cache: any) => {
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_button = _resolveComponent("n-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!templateLoaded.value)
      ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 0 }))
      : _createCommentVNode("", true),
    (templateLoaded.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createElementVNode("nav", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("h4", _hoisted_5, [
                _createVNode(EditableText, {
                  value: templateName.value,
                  placeholder: 'Enter template name...',
                  onTextUpdated: onNameChange
                }, null, 8, ["value"])
              ])
            ]),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("p", _hoisted_7, [
                _createVNode(_component_n_button, {
                  id: "save-content-button",
                  variant: "secondary",
                  loading: updateInProgress.value,
                  onClick: saveTemplate
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", null, _toDisplayString(templateId.value ? 'Save' : 'Create'), 1)
                  ]),
                  _: 1
                }, 8, ["loading"])
              ]),
              _createElementVNode("p", _hoisted_8, [
                _createVNode(_component_n_button, {
                  id: "cancel-save-content-button",
                  variant: "secondary--outline",
                  onClick: cancel
                }, {
                  default: _withCtx(() => _cache[1] || (_cache[1] = [
                    _createElementVNode("span", null, "Cancel", -1)
                  ])),
                  _: 1
                })
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (templateLoaded.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", { class: "column is-6" }, [
                _cache[2] || (_cache[2] = _createElementVNode("div", { class: "title is-5" }, "HTML", -1)),
                _createElementVNode("div", { id: EDITOR_ID })
              ]),
              _createElementVNode("div", _hoisted_12, [
                _cache[3] || (_cache[3] = _createElementVNode("div", { class: "title is-5" }, "Preview", -1)),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("iframe", {
                    title: "preview",
                    srcdoc: previewHtml.value,
                    sandbox: ""
                  }, null, 8, _hoisted_14)
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    (showDiscardChangesModel.value)
      ? (_openBlock(), _createBlock(ConfirmationModal, {
          key: 3,
          confirmationTitle: "Discard changes",
          confirmationMessage: "Are you sure you wish to discard your changes",
          onConfirm: goBackToEmailSettings,
          onCancel: _cache[0] || (_cache[0] = ($event: any) => (showDiscardChangesModel.value = false))
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})